import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { useEditorTheme } from "../theme.js";
import { marked } from "marked";
import ProblemStatement from "../components/ProblemStatement.js";
import McqAnswer from "../components/McqAnswer.js";
import { useProctoring } from "../useProctoring.js";
import { api, type Exam, type ExamAttempt, type ExamQuestion, type RunResult } from "../api.js";

interface AnswerRef {
  examQuestionId: string;
  submissionId: string | null;
}

/**
 * The student's exam session: question list, editor, per-question submit, and a
 * countdown. The countdown is cosmetic -- the server owns the deadline and
 * re-syncs it on every load, so a tampered client clock changes nothing.
 */
export default function ExamRunner() {
  const editorTheme = useEditorTheme();
  const { id } = useParams<{ id: string }>();

  const [exam, setExam] = useState<Exam | null>(null);
  const [attempt, setAttempt] = useState<ExamAttempt | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [answers, setAnswers] = useState<AnswerRef[]>([]);
  const [current, setCurrent] = useState(0);
  const [code, setCode] = useState("");
  const [stdin, setStdin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | "run" | "submit" | "finish">(null);
  const [output, setOutput] = useState<{ diagnostics: string; run: RunResult | null } | null>(null);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  const [notStarted, setNotStarted] = useState(false);
  const [finished, setFinished] = useState<{ finalScore: number | null } | null>(null);
  /** Chosen options per MCQ, kept per question so switching does not lose them. */
  const [chosen, setChosen] = useState<Record<string, string[]>>({});
  const [mcqStatus, setMcqStatus] = useState<string | null>(null);

  const savedCode = useRef("");
  /**
   * Autosaved work per question, as the server last gave it to us.
   *
   * A ref rather than state: switching question reads it synchronously to seed
   * the editor, and it must not be a render behind. It previously lived on
   * `window.__drafts`, which worked by accident -- nothing owned it, nothing
   * cleared it between exams, and a second runner mounting would have read the
   * first one's work.
   */
  const drafts = useRef<Map<string, { code: string; selectedOptions: string }>>(new Map());
  const [locked, setLocked] = useState(false);
  /** Server deadline minus local clock at load, so the countdown ignores clock skew. */
  const clockOffset = useRef(0);

  const proctor = useProctoring({
    examId: id,
    active: !!attempt && !finished && !locked,
    requireFullscreen: exam?.requireFullscreen ?? false,
    blockCopyPaste: exam?.blockCopyPaste ?? false,
    captureOnFocusLoss: exam?.captureOnFocusLoss ?? false,
    onAutoSubmit: () => setFinished({ finalScore: null }),
    onLock: () => setLocked(true),
  });

  // ------------------------------------------------------------ load

  const loadAttempt = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api.get<{
        attempt: ExamAttempt;
        questions: ExamQuestion[];
        answers: AnswerRef[];
        drafts: { assignmentId: string; code: string; selectedOptions: string }[];
        serverTime: string;
      }>(`/api/exams/${id}/attempt`);

      clockOffset.current = new Date(r.serverTime).getTime() - Date.now();
      setAttempt(r.attempt);
      setQuestions(r.questions);
      setAnswers(r.answers);
      setNotStarted(false);

      drafts.current = new Map(
        r.drafts.map((d) => [d.assignmentId, { code: d.code, selectedOptions: d.selectedOptions }])
      );

      // A resumed attempt comes back with the student's autosaved work -- their
      // code, and the options they had ticked.
      setChosen(
        Object.fromEntries(
          r.drafts
            .filter((d) => d.selectedOptions)
            .map((d) => [d.assignmentId, d.selectedOptions.split(",")])
        )
      );

      const first = r.questions[0];
      if (first) {
        const initial = drafts.current.get(first.assignment.id)?.code || first.assignment.starterCode || "";
        setCode(initial);
        savedCode.current = initial;
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      if (msg.includes("not started")) setNotStarted(true);
      else setError(msg);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api
      .get<{ exam: Exam; attempt: ExamAttempt | null }>(`/api/exams/${id}`)
      .then((r) => {
        setExam(r.exam);
        if (!r.attempt) setNotStarted(true);
        else if (r.attempt.status !== "IN_PROGRESS") setFinished({ finalScore: r.attempt.finalScore ?? null });
        else void loadAttempt();
      })
      .catch((e) => setError(e.message));
  }, [id, loadAttempt]);

  // ------------------------------------------------------- countdown

  useEffect(() => {
    if (!attempt) return;
    const tick = () => {
      const now = Date.now() + clockOffset.current;
      const left = new Date(attempt.deadlineAt).getTime() - now;
      setRemainingMs(left);
      // The server auto-submits via the sweeper; this just stops the student
      // typing into a session that is already over.
      if (left <= 0) setFinished((f) => f ?? { finalScore: null });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [attempt]);

  // ------------------------------------------------------ autosave

  useEffect(() => {
    const q = questions[current];
    if (!q || code === savedCode.current || finished) return;
    const timer = setTimeout(() => {
      api
        .put(`/api/assignments/${q.assignment.id}/draft`, { code })
        .then(() => (savedCode.current = code))
        .catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [code, current, questions, finished]);

  /**
   * Keep the local copy in step with what is being typed.
   *
   * Without this, switching away from a question and back restored it as it
   * stood when the attempt loaded -- the student's work since then vanished
   * from the screen, and typing again wrote the stale text back over the
   * server's good copy. The autosave above was doing its job; nothing was
   * telling the cache.
   */
  useEffect(() => {
    const q = questions[current];
    if (!q) return;
    const existing = drafts.current.get(q.assignment.id);
    drafts.current.set(q.assignment.id, { code, selectedOptions: existing?.selectedOptions ?? "" });
  }, [code, current, questions]);

  /**
   * Persist ticked options as they are ticked.
   *
   * Not debounced like the code autosave: choosing an option is one click, not a
   * stream of keystrokes, and the whole point is that it survives a reload the
   * student did not plan.
   */
  function saveMcqDraft(assignmentId: string, ids: string[]) {
    const existing = drafts.current.get(assignmentId);
    drafts.current.set(assignmentId, {
      code: existing?.code ?? "",
      selectedOptions: ids.join(","),
    });
    api.put(`/api/assignments/${assignmentId}/draft`, { selectedOptionIds: ids }).catch(() => {});
  }

  function switchTo(index: number) {
    const q = questions[index];
    if (!q) return;
    const next = drafts.current.get(q.assignment.id)?.code || q.assignment.starterCode || "";
    setCurrent(index);
    setCode(next);
    savedCode.current = next;
    setOutput(null);
    setMcqStatus(null);
  }

  // -------------------------------------------------------- actions

  async function start() {
    if (!id) return;
    setError(null);
    // Fullscreen is requested before anything is awaited. Safari only honours
    // the request while the click that caused it is still "live", and an
    // awaited fetch ends that -- which is why asking afterwards silently does
    // nothing there.
    if (exam?.requireFullscreen) await proctor.enterFullscreen();
    try {
      await api.post(`/api/exams/${id}/start`);
      await loadAttempt();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start the exam.");
    }
  }

  async function run() {
    const q = questions[current];
    if (!q) return;
    setBusy("run");
    try {
      const r = await api.post<{ compiled: boolean; diagnostics: string; run: RunResult | null }>(
        `/api/assignments/${q.assignment.id}/run`,
        { code, stdin }
      );
      setOutput({ diagnostics: r.diagnostics, run: r.run });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Run failed.");
    } finally {
      setBusy(null);
    }
  }

  async function submitAnswer() {
    const q = questions[current];
    if (!q) return;
    const mcq = q.assignment.kind === "MCQ";
    if (mcq && (chosen[q.assignment.id] ?? []).length === 0) {
      setError("Choose an answer first.");
      return;
    }
    setBusy("submit");
    setError(null);
    try {
      const r = await api.post<{ submission: { id: string }; score: number }>(
        `/api/assignments/${q.assignment.id}/submit`,
        mcq ? { selectedOptionIds: chosen[q.assignment.id] ?? [] } : { code }
      );
      setAnswers((prev) => {
        const next = prev.filter((a) => a.examQuestionId !== q.id);
        return [...next, { examQuestionId: q.id, submissionId: r.submission.id }];
      });
      // Scores stay hidden during an exam; confirming receipt is the useful signal.
      setOutput({ diagnostics: "Answer submitted. You can revise and submit again.", run: null });
      setMcqStatus("Answer submitted. You can change it and submit again.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Submit failed.");
    } finally {
      setBusy(null);
    }
  }

  async function finish() {
    if (!id) return;
    const answered = answers.filter((a) => a.submissionId).length;
    if (
      !confirm(
        `Finish and submit the exam?\n\nYou have answered ${answered} of ${questions.length} questions. This cannot be undone.`
      )
    )
      return;
    setBusy("finish");
    try {
      const r = await api.post<{ attempt: { finalScore: number | null } }>(`/api/exams/${id}/finish`);
      setFinished({ finalScore: r.attempt.finalScore });
      proctor.exitFullscreen();
      proctor.stopScreenShare();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not finish the exam.");
    } finally {
      setBusy(null);
    }
  }

  // ----------------------------------------------------------- views

  if (error && !exam) return <div className="page"><div className="error-banner">{error}</div></div>;
  if (!exam) return <div className="page muted">Loading…</div>;

  if (locked) {
    return (
      <div className="page">
        <div className="card" style={{ textAlign: "center", padding: 40 }}>
          <h1 style={{ marginTop: 0 }}>Attempt locked</h1>
          <p className="muted">
            This attempt exceeded the allowed number of violations and has been locked. Your work
            so far is saved. Speak to your invigilator.
          </p>
          <Link to="/assignments"><button>Back to assignments</button></Link>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="page">
        <div className="card" style={{ textAlign: "center", padding: 40 }}>
          <h1 style={{ marginTop: 0 }}>Exam submitted</h1>
          <p className="muted">Your answers have been recorded.</p>
          {exam.showResultsToStudent === "IMMEDIATELY" && finished.finalScore !== null && (
            <p style={{ fontSize: 20 }}><strong>{finished.finalScore}</strong> marks</p>
          )}
          {exam.showResultsToStudent === "AFTER_CLOSE" && (
            <p className="muted">
              Results will be available after the exam closes — from the exam on your assignments
              page.
            </p>
          )}
          <div className="row" style={{ justifyContent: "center" }}>
            <Link to="/assignments"><button className="primary">Back to assignments</button></Link>
            {exam.showResultsToStudent !== "NEVER" && (
              <Link to={`/exams/${exam.id}/result`}><button>See your result</button></Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (notStarted) {
    const now = Date.now();
    const opens = new Date(exam.opensAt).getTime();
    const closes = new Date(exam.closesAt).getTime();
    const tooEarly = now < opens;
    const tooLate = now > closes;

    return (
      <div className="page">
        <Link to="/assignments" className="muted" style={{ fontSize: 13 }}>← All assignments</Link>
        <div className="card" style={{ marginTop: 12 }}>
          <h1 style={{ marginTop: 0 }}>{exam.title}</h1>
          <div
            className="desc"
            dangerouslySetInnerHTML={{ __html: marked.parse(exam.description || "") as string }}
          />

          <div className="notice">
            <strong>Before you begin</strong>
            <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
              <li>
                Duration:{" "}
                {exam.durationMinutes ? `${exam.durationMinutes} minutes from when you press Start` : "until the exam closes"}.
              </li>
              <li>
                <strong>The clock keeps running if you disconnect.</strong> Closing the browser or a
                machine restart will not pause or reset it.
              </li>
              <li>Your work is saved automatically as you type.</li>
              <li>
                <strong>
                  Switching tabs or applications, leaving this window, and exiting fullscreen are
                  each recorded
                </strong>{" "}
                and shown to your faculty, with the time they happened.
                {exam.maxViolations > 0 && (
                  <>
                    {" "}
                    You are allowed {exam.maxViolations}.
                    {exam.onViolationExceeded === "AUTO_SUBMIT" &&
                      " After that, your exam is submitted automatically."}
                    {exam.onViolationExceeded === "LOCK" &&
                      " After that, your attempt is locked and you must speak to an invigilator."}
                  </>
                )}
              </li>
              {exam.blockCopyPaste && <li>Copy and paste are disabled during this exam.</li>}
              {exam.requireFullscreen && (
                <li>
                  This exam runs in fullscreen. Pressing Start will switch your browser to
                  fullscreen — leave it and you will be warned.
                </li>
              )}
            </ul>
          </div>

          {error && <div className="error-banner">{error}</div>}
          {tooEarly && <div className="error-banner">This exam opens at {new Date(exam.opensAt).toLocaleString()}.</div>}
          {tooLate && <div className="error-banner">This exam closed at {new Date(exam.closesAt).toLocaleString()}.</div>}

          {exam.captureOnFocusLoss && (
            <div className="card" style={{ background: "var(--bg)" }}>
              <strong>Screen recording</strong>
              <p className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                This exam takes a picture of your screen at the moment you leave the exam window —
                not continuously, and never while you are working in it. Your faculty can see those
                pictures; they are deleted automatically after the retention period. Your browser
                will show that sharing is active the whole time, and you can stop it at any point
                from the browser itself — stopping is recorded.
              </p>
              <p className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
                Please choose <strong>your entire screen</strong> when asked. Sharing a single tab
                records only that tab, and is noted as a partial share.
              </p>
              <div className="row">
                <button onClick={() => void proctor.requestScreenShare()}>
                  {proctor.shareState === "GRANTED"
                    ? "Screen sharing on"
                    : proctor.shareState === "PARTIAL"
                      ? "Only a tab is shared — share the whole screen"
                      : "Allow screen recording"}
                </button>
                {proctor.shareState === "GRANTED" && <span className="badge pass">ready</span>}
                {proctor.shareState === "PARTIAL" && <span className="badge warn">partial</span>}
                {(proctor.shareState === "DECLINED" || proctor.shareState === "STOPPED") && (
                  <span className="badge fail">not shared</span>
                )}
                {proctor.shareState === "UNSUPPORTED" && (
                  <span className="badge fail">not supported by this browser</span>
                )}
              </div>
              {proctor.shareState !== "NOT_ASKED" && proctor.shareState !== "GRANTED" && (
                <p className="muted" style={{ fontSize: 13, margin: "10px 0 0" }}>
                  You can still sit the exam. That you did not share your screen is recorded for
                  your invigilator.
                </p>
              )}
            </div>
          )}

          <button className="primary" onClick={start} disabled={tooEarly || tooLate}>
            Start exam
          </button>
        </div>
      </div>
    );
  }

  const q = questions[current];
  const answeredIds = new Set(answers.filter((a) => a.submissionId).map((a) => a.examQuestionId));
  const urgent = remainingMs !== null && remainingMs < 5 * 60_000;

  const outOfFullscreen = exam.requireFullscreen && !proctor.inFullscreen && !proctor.fullscreenUnsupported;

  return (
    <div className="exam-shell">
      {(proctor.lastKind || outOfFullscreen || proctor.violationCount > 0) && (
        <div className={`proctor-bar ${proctor.violationCount > exam.maxViolations ? "over" : ""}`}>
          <span>
            {proctor.lastKind === "TAB_SWITCH" && "You left the exam window."}
            {proctor.lastKind === "BLUR" && "The exam window lost focus."}
            {proctor.lastKind === "FULLSCREEN_EXIT" && "You left fullscreen."}
            {proctor.lastKind === "COPY" && "Copying is not allowed in this exam."}
            {proctor.lastKind === "PASTE" && "Pasting is not allowed in this exam."}
            {!proctor.lastKind && outOfFullscreen && "This exam is meant to be sat in fullscreen."}
            {!proctor.lastKind && !outOfFullscreen && "Recorded activity"}
          </span>
          <strong>
            {proctor.violationCount} of {exam.maxViolations} allowed
          </strong>
          {proctor.violationCount > exam.maxViolations && (
            <span className="badge fail">
              {exam.onViolationExceeded === "AUTO_SUBMIT"
                ? "next one submits your exam"
                : exam.onViolationExceeded === "LOCK"
                  ? "next one locks your attempt"
                  : "reported to your faculty"}
            </span>
          )}
          {outOfFullscreen && (
            <button onClick={() => void proctor.enterFullscreen()}>Return to fullscreen</button>
          )}
        </div>
      )}

      {exam.captureOnFocusLoss && (
        <div className="proctor-bar">
          <span>
            {proctor.shareState === "GRANTED" || proctor.shareState === "PARTIAL"
              ? `Screen recording is on. ${proctor.shotCount} picture${proctor.shotCount === 1 ? "" : "s"} taken — one each time you leave this window.`
              : "Your screen is not being shared. That is recorded for your invigilator."}
          </span>
          {proctor.shareState !== "GRANTED" && (
            <button onClick={() => void proctor.requestScreenShare()}>Share screen</button>
          )}
        </div>
      )}

      {proctor.fullscreenUnsupported && exam.requireFullscreen && (
        <div className="proctor-bar">
          <span>
            This browser cannot go fullscreen. Your faculty asked for a fullscreen exam — use a
            laptop browser if you can. Everything else is still recorded.
          </span>
        </div>
      )}

    <div className="workspace">
      <div className="pane">
        <div className="between" style={{ marginBottom: 10 }}>
          <strong style={{ fontSize: 15 }}>{exam.title}</strong>
        </div>

        <div
          className="card"
          style={{
            textAlign: "center",
            padding: 12,
            marginBottom: 14,
            borderColor: urgent ? "var(--fail)" : "var(--border)",
          }}
        >
          <div className="muted" style={{ fontSize: 12 }}>TIME REMAINING</div>
          <div
            style={{
              fontSize: 26,
              fontVariantNumeric: "tabular-nums",
              color: urgent ? "var(--fail)" : "var(--text)",
              fontWeight: 600,
            }}
          >
            {formatRemaining(remainingMs)}
          </div>
        </div>

        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>QUESTIONS</div>
        {questions.map((question, i) => (
          <div
            key={question.id}
            className="test-row"
            style={{ cursor: "pointer", borderColor: i === current ? "var(--accent)" : "var(--border)" }}
            onClick={() => switchTo(i)}
          >
            <span className={`badge ${answeredIds.has(question.id) ? "pass" : "draft"}`}>
              {answeredIds.has(question.id) ? "done" : i + 1}
            </span>
            <span className="name" style={{ fontSize: 13 }}>{question.assignment.title}</span>
            <span className="muted" style={{ fontSize: 12 }}>{question.marks}</span>
          </div>
        ))}

        <button className="primary" style={{ width: "100%", marginTop: 14 }} onClick={finish} disabled={busy !== null}>
          {busy === "finish" ? "Submitting…" : "Finish exam"}
        </button>

        {q && (
          <div style={{ marginTop: 18, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <ProblemStatement
              description={q.assignment.description ?? ""}
              inputFormat={q.assignment.inputFormat}
              outputFormat={q.assignment.outputFormat}
              testCases={q.assignment.testCases}
            />
          </div>
        )}
      </div>

      <div className="editor-column">
        <div className="toolbar">
          <span style={{ fontWeight: 600 }}>{q?.assignment.title}</span>
          <span className="muted" style={{ fontSize: 12 }}>{q?.marks} marks</span>
          <div style={{ flex: 1 }} />
          {q?.assignment.kind !== "MCQ" && (
            <button onClick={run} disabled={busy !== null}>{busy === "run" ? "Running…" : "Run ▸"}</button>
          )}
          <button className="primary" onClick={submitAnswer} disabled={busy !== null}>
            {busy === "submit" ? "Submitting…" : "Submit answer"}
          </button>
        </div>

        {q?.assignment.kind === "MCQ" ? (
          <div style={{ overflow: "auto", padding: 16 }}>
            {(q.assignment.expectedAnswers ?? 0) > 1 && (
              <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
                Choose {q.assignment.expectedAnswers} options. All of them must be right.
              </p>
            )}
            <McqAnswer
              options={q.assignment.options ?? []}
              selected={chosen[q.assignment.id] ?? []}
              onChange={(ids) => {
                setChosen((prev) => ({ ...prev, [q.assignment.id]: ids }));
                saveMcqDraft(q.assignment.id, ids);
              }}
              multiple={(q.assignment.expectedAnswers ?? 0) > 1}
              disabled={busy !== null}
            />
            {mcqStatus && <div className="notice" style={{ marginTop: 14 }}>{mcqStatus}</div>}
            {error && <div className="error-banner" style={{ marginTop: 14 }}>{error}</div>}
          </div>
        ) : (
        <Editor
          language="c"
          theme={editorTheme}
          value={code}
          onChange={(v) => setCode(v ?? "")}
          options={{
            fontSize: 13.5,
            minimap: { enabled: false },
            tabSize: 4,
            scrollBeyondLastLine: false,
            automaticLayout: true,
          }}
        />
        )}

        {q?.assignment.kind !== "MCQ" && (
        <div className="output">
          <div className="tabs"><button className="active">Output</button></div>
          <div className="output-body">
            {error && <div className="error-banner">{error}</div>}
            <div className="grid-2">
              <label style={{ marginBottom: 0 }}>
                <span>Input (stdin)</span>
                <textarea rows={5} value={stdin} onChange={(e) => setStdin(e.target.value)} />
              </label>
              <div>
                <span className="muted" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Result</span>
                <pre className={`console ${output ? "" : "empty"}`}>
                  {output
                    ? [output.diagnostics, output.run?.stdout].filter(Boolean).join("\n") || "(no output)"
                    : "Run your code to see output here."}
                </pre>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
    </div>
  );
}

function formatRemaining(ms: number | null): string {
  if (ms === null) return "--:--";
  if (ms <= 0) return "00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
